<template>
  <div class="faq-container">
    <div class="header">
      <router-link :to="{ name: 'chat' }" class="home-link">
        <img src="/static/unstruct-logo-new.png" alt="UnStruct Logo" width="45" height="45" />
      </router-link>
      <h1>Subscription & Billing FAQ</h1>
    </div>

    <div class="preamble">
      <h2>Welcome to UnStruct</h2>
      <p>
        Get answers to common questions about your account, subscription options, and billing.
        Our goal is to provide a seamless experience while you explore the full potential of UnStruct.
      </p>

      <v-alert type="info" outlined class="mb-4" color="#7b97ff">
        <strong>Quick Tip:</strong> UnStruct offers flexible subscription options to match your needs, from free anonymous access to Pro and Team plans with enhanced capabilities.
      </v-alert>
    </div>

    <div class="search-container mb-5">
      <v-text-field
        v-model="searchQuery"
        label="Search FAQs"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        clearable
        hide-details
        class="search-field"
        color="#7b97ff"
      ></v-text-field>
    </div>

    <div class="faq-panels">
      <v-expansion-panels flat>
        <v-expansion-panel
          v-for="(faq, index) in filteredFaqs"
          :key="index"
          class="faq-panel"
        >
          <v-expansion-panel-header class="faq-question">
            <div class="d-flex align-center">
              <v-icon
                :color="getCategoryColor(faq.category)"
                class="mr-3"
              >
                {{ getCategoryIcon(faq.category) }}
              </v-icon>
              <span>{{ faq.question }}</span>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content class="faq-answer">
            <!-- Subscription Plans FAQ -->
            <div v-if="faq.id === 'plans'">
              <div class="plan-comparison">
                <div class="plan-tier">
                  <div class="plan-header">
                    <v-icon small>mdi-rocket-launch-outline</v-icon>
                    <span class="plan-name">Free</span>
                    <span class="plan-price">$0/month</span>
                  </div>
                  <ul class="plan-features">
                    <li>5 file & text submissions</li>
                    <li>3 AI-powered video queries</li>
                    <li>Limited to 3 video segments</li>
                    <li>15 search operations</li>
                    <li>5 projects & galaxies</li>
                  </ul>
                  <div class="plan-note">Great for trying out the platform</div>
                </div>

                <div class="plan-tier featured">
                  <div class="plan-header">
                    <v-icon small>mdi-star-three-points-outline</v-icon>
                    <span class="plan-name">Pro</span>
                    <span class="plan-price">$149/month</span>
                  </div>
                  <ul class="plan-features">
                    <li>3,000 AI-powered video queries</li>
                    <li>Unlimited video segments</li>
                    <li>500 file & text submissions</li>
                    <li>Premium LLM access (1,500 queries)</li>
                    <li>Advanced customization</li>
                    <li>Unlimited projects</li>
                  </ul>
                  <div class="plan-note">Perfect for professionals & creators</div>
                </div>

                <div class="plan-tier">
                  <div class="plan-header">
                    <v-icon small>mdi-account-group</v-icon>
                    <span class="plan-name">Pro Team</span>
                    <span class="plan-price">$399/month</span>
                  </div>
                  <ul class="plan-features">
                    <li>Everything in Pro, plus:</li>
                    <li>Collaboration for 5 team members</li>
                    <li>6,000 AI-powered video queries</li>
                    <li>Premium LLM access (3,000 queries)</li>
                    <li>Advanced team analytics</li>
                    <li>Dedicated account manager</li>
                  </ul>
                  <div class="plan-note">Best for teams and organizations</div>
                </div>
              </div>
            </div>

            <!-- Anonymous Access FAQ -->
            <div v-else-if="faq.id === 'anonymous'">
              <p>UnStruct offers a unique privacy-first approach with anonymous access:</p>
              <ul>
                <li><strong>No registration required:</strong> Start using immediately without creating an account</li>
                <li><strong>Secure session:</strong> Access is controlled by a secure anonymous key stored in your browser</li>
                <li><strong>Data privacy:</strong> No personal data collection or tracking</li>
                <li><strong>Limited features:</strong> Access most basic features without an account</li>
              </ul>
              <p class="note-text">
                <v-icon small color="warning">mdi-alert-circle-outline</v-icon>
                Important: If you clear your browser data, you'll lose access to your anonymous key and all associated data.
              </p>
            </div>

            <!-- Pro Upgrade FAQ -->
            <div v-else-if="faq.id === 'anonymous-pro'">
              <p>No, upgrading to Pro requires a registered account. Here's why:</p>
              <ul>
                <li>If you lose your anonymous key, you'd also lose your paid subscription</li>
                <li>Registered accounts provide secure access to your subscription across devices</li>
                <li>Billing information needs to be associated with an identifiable account</li>
              </ul>
              <p>To upgrade:</p>
              <ol>
                <li>Register with an email address to create a new account</li>
                <li>Choose your subscription plan</li>
                <li>Complete the secure payment process</li>
              </ol>
              <p class="note-text">You can cancel your subscription anytime with one click from within the app.</p>
            </div>

            <!-- Team Plan FAQ -->
            <div v-else-if="faq.id === 'team-plan'">
              <p>Yes, you can start with a single user on the Pro Team plan and add more members as needed:</p>
              <ul>
                <li>The Pro Team plan begins with a single user</li>
                <li>You can add team members at any time</li>
                <li>Billing is automatically adjusted based on the number of users/seats</li>
                <li>If you're the only user, you'll be billed for just a single user</li>
                <li>Each additional team member is $59/month</li>
              </ul>
              <p>The Team plan is ideal if you plan to collaborate with others or need additional capacity beyond the standard Pro plan.</p>
            </div>

            <!-- Cancel Subscription FAQ -->
            <div v-else-if="faq.id === 'cancel'">
              <p>You can cancel your subscription at any time with these simple steps:</p>
              <ol>
                <li>Click on your profile icon in the top right corner</li>
                <li>Select "Cancel Subscription" from the dropdown menu</li>
                <li>Confirm your cancellation in the dialog that appears</li>
              </ol>
              <p class="note-text">
                <v-icon small color="info">mdi-information-outline</v-icon>
                Important: After cancellation, your Pro features will remain accessible until the end of your current billing period. No refunds are provided for partial months.
              </p>
              <p>You can reactivate your subscription anytime before the end date to avoid interruption of Pro features.</p>
            </div>

            <!-- Reactivate Subscription FAQ -->
            <div v-else-if="faq.id === 'reactivate'">
              <p>If you've canceled your subscription but want to reactivate it:</p>
              <ol>
                <li>Click on your profile icon in the top right corner</li>
                <li>Select "Reactivate Subscription" from the dropdown menu</li>
                <li>Choose your desired plan (Pro or Pro Team)</li>
                <li>Confirm the reactivation</li>
              </ol>
              <p class="highlight-text">Reactivating before your subscription end date will ensure uninterrupted access to all Pro features.</p>
              <p>If your subscription has already expired, you'll be guided through creating a new subscription.</p>
            </div>

            <!-- Expired Subscription FAQ -->
            <div v-else-if="faq.id === 'expired'">
              <p>When your subscription ends:</p>
              <ul>
                <li>Your account remains active but reverts to the free plan</li>
                <li>All your projects, files, and data remain stored and accessible</li>
                <li>You'll be limited to the free plan's features and usage limits</li>
                <li>Pro-only features will no longer be accessible until you resubscribe</li>
              </ul>
              <p class="note-text">We retain your data for a period of time after subscription ends to make reactivation seamless. If you wish to delete your account and data entirely, you can request this from your account settings.</p>
            </div>

            <!-- Convert Anonymous Account FAQ -->
            <div v-else-if="faq.id === 'convert'">
              <p>Currently, there's no direct way to convert an anonymous account to a registered one:</p>
              <ul>
                <li>Anonymous accounts and registered accounts exist separately</li>
                <li>Your anonymous data is tied to your browser's local storage</li>
                <li>For persistent access and cross-device sync, we recommend starting with a registered account</li>
              </ul>
              <p class="note-text">If you have important data in an anonymous account that you want to preserve, consider exporting any critical content before creating a registered account.</p>
            </div>

            <!-- Payment Methods FAQ -->
            <div v-else-if="faq.id === 'payment'">
              <p>We accept the following payment methods for all subscriptions:</p>
              <div class="payment-row">
                <div class="payment-card">Visa</div>
                <div class="payment-card">Mastercard</div>
                <div class="payment-card">American Express</div>
                <div class="payment-card">Discover</div>
                <div class="payment-card">PayPal</div>
              </div>
              <p>All payments are processed securely through Stripe, an industry-leading payment processor that ensures your financial information remains safe and protected.</p>
              <p class="highlight-text">We never store your complete credit card information on our servers.</p>
            </div>

            <!-- Security FAQ -->
            <div v-else-if="faq.id === 'security'">
              <p>Your financial security is our priority:</p>
              <ul>
                <li>All payment processing is handled by Stripe, a PCI-compliant payment processor</li>
                <li>Your complete credit card information is never stored on our servers</li>
                <li>All payment transactions are encrypted using industry-standard SSL/TLS</li>
                <li>We maintain strict internal access controls for customer billing data</li>
              </ul>
              <p class="note-text">
                <v-icon small color="success">mdi-shield-check</v-icon>
                We follow industry best practices to ensure your payment information remains safe and protected at all times.
              </p>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div v-if="filteredFaqs.length === 0" class="no-results">
        <v-icon large color="grey lighten-1">mdi-file-search-outline</v-icon>
        <p>No FAQs matching "{{ searchQuery }}"</p>
        <v-btn text color="#7b97ff" @click="searchQuery = ''">Clear Search</v-btn>
      </div>
    </div>

    <div class="contact-support mt-8">
      <v-card outlined class="support-card" elevation="2" color="rgba(30, 30, 35, 0.6)">
        <v-card-text class="d-flex align-center">
          <v-avatar color="#7b97ff" size="56" class="mr-4">
            <v-icon>mdi-headset</v-icon>
          </v-avatar>
          <div>
            <h3 class="subtitle-1 font-weight-bold mb-1">Still have questions?</h3>
            <p class="mb-2">Our support team is ready to help with any subscription or billing issues.</p>
            <div class="contact-buttons">
              <v-btn color="#7b97ff" small class="mr-3" @click="contactSupport">
                <v-icon small left>mdi-email-outline</v-icon>
                Contact Support
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SubscriptionFAQ',

  data() {
    return {
      searchQuery: '',
      faqs: [
        {
          id: 'plans',
          question: "What subscription plans does UnStruct offer?",
          category: 'subscription'
        },
        {
          id: 'anonymous',
          question: "How does anonymous access work?",
          category: 'account'
        },
        {
          id: 'anonymous-pro',
          question: "Can I upgrade to Pro while staying anonymous?",
          category: 'account'
        },
        {
          id: 'team-plan',
          question: "Can the 'Pro Team' plan have only a single user?",
          category: 'subscription'
        },
        {
          id: 'cancel',
          question: "How do I cancel my subscription?",
          category: 'billing'
        },
        {
          id: 'reactivate',
          question: "How do I reactivate my canceled subscription?",
          category: 'billing'
        },
        {
          id: 'expired',
          question: "What happens to my data when my subscription ends?",
          category: 'account'
        },
        {
          id: 'convert',
          question: "Can I convert my anonymous account to a registered account?",
          category: 'account'
        },
        {
          id: 'payment',
          question: "What payment methods do you accept?",
          category: 'billing'
        },
        {
          id: 'security',
          question: "How is my billing information secured?",
          category: 'billing'
        }
      ]
    };
  },

  computed: {
    filteredFaqs() {
      if (!this.searchQuery) return this.faqs;

      const query = this.searchQuery.toLowerCase();
      return this.faqs.filter(faq =>
        faq.question.toLowerCase().includes(query)
      );
    }
  },

  methods: {
    contactSupport() {
      window.location.href = 'mailto:support@unstruct.ai';
    },
    callKishore() {
      window.open('tel:+16504306223', '_blank');
    },
    getCategoryIcon(category) {
      const icons = {
        'subscription': 'mdi-package-variant-closed',
        'billing': 'mdi-credit-card-outline',
        'account': 'mdi-account-outline'
      };
      return icons[category] || 'mdi-help-circle-outline';
    },

    getCategoryColor(category) {
      const colors = {
        'subscription': '#7b97ff',
        'billing': '#43d6b5',
        'account': '#b76cc5'
      };
      return colors[category] || 'grey';
    }
  }
};
</script>

<style scoped>
.faq-container {
  max-width: 900px;
  margin: 0 auto 80px auto;
  padding: 20px;
  font-family: 'Roboto', sans-serif;
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.home-link {
  margin-right: 16px;
}

.header h1 {
  font-size: 28px;
  font-weight: 500;
  margin: 0;
}

.preamble {
  background: linear-gradient(135deg, rgba(61, 90, 254, 0.1), rgba(61, 90, 254, 0.05));
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(91, 122, 252, 0.2);
}

.preamble h2 {
  color: #7b97ff;
  margin-top: 0;
  font-size: 22px;
  font-weight: 500;
}

.search-container {
  margin-bottom: 24px;
}

.search-field {
  max-width: 500px;
  margin: 0 auto;
}

.faq-panels {
  border-radius: 12px;
  overflow: hidden;
  background-color: rgba(30, 30, 35, 0.4);
  box-shadow: 0 3px 20px rgba(0, 0, 0, 0.2);
}

.faq-panel {
  margin-bottom: 2px;
  transition: all 0.2s ease;
}

.faq-question {
  font-size: 16px;
  font-weight: 500;
}

.faq-answer {
  padding: 0 16px 16px;
  line-height: 1.6;
}

.no-results {
  padding: 40px;
  text-align: center;
  color: #a0a0a0;
}

.no-results p {
  margin: 16px 0;
}

.plan-comparison {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 16px 0;
  justify-content: space-between;
}

.plan-tier {
  flex: 1 1 200px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  background-color: rgba(30, 30, 35, 0.4);
}

.plan-tier.featured {
  border-color: #7b97ff;
  background-color: rgba(123, 151, 255, 0.1);
  position: relative;
  box-shadow: 0 3px 15px rgba(123, 151, 255, 0.2);
}

.plan-tier.featured::before {
  content: "POPULAR";
  position: absolute;
  top: -10px;
  right: 10px;
  background: #7b97ff;
  color: #1a1a1a;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: bold;
}

.plan-header {
  margin-bottom: 16px;
  text-align: center;
}

.plan-name {
  font-size: 18px;
  font-weight: 500;
  display: block;
  margin: 6px 0;
}

.plan-price {
  color: #7b97ff;
  font-weight: bold;
  display: block;
}

.plan-features {
  padding-left: 16px;
  margin: 0;
  line-height: 1.6;
}

.plan-note {
  text-align: center;
  margin-top: 16px;
  font-style: italic;
  color: #a0a0a0;
  font-size: 14px;
}

.payment-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 16px 0;
}

.payment-card {
  background-color: rgba(40, 40, 45, 0.8);
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 500;
}

.note-text {
  background-color: rgba(40, 40, 45, 0.5);
  padding: 12px;
  border-radius: 8px;
  margin: 16px 0;
  border-left: 3px solid #6e6e6e;
  font-size: 14px;
}

.highlight-text {
  background-color: rgba(123, 151, 255, 0.15);
  padding: 12px;
  border-radius: 8px;
  margin: 16px 0;
  border-left: 3px solid #7b97ff;
  font-weight: 500;
}

.contact-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 600px) {
  .plan-comparison {
    flex-direction: column;
  }

  .header h1 {
    font-size: 24px;
  }

  .preamble {
    padding: 16px;
  }

  .payment-row {
    justify-content: center;
  }

  .contact-buttons {
    flex-direction: column;
    align-items: flex-start;
  }

  .contact-buttons .v-btn {
    margin: 4px 0 !important;
  }
}
</style>
